import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  AvatarBadge,
  Box,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from '@chakra-ui/react';
import { GenericInputControl } from 'components/RabbitOKRSidePanel/Components/ParentOKRComponent';
import SearchBar from 'components/RabbitOKRSidePanel/Components/SearchBar';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { difference, debounce } from 'lodash';

import { Collaborators, Cross, PeerSettingsV2 } from 'assets/assetsV1/iconComponents';

import { modifyString, StringModifier } from 'constants/copyHelpers';
import { isMobileView } from 'constants/isMobileView';

import { Avatar } from 'uiToolkitV1/Avatar';
import { AvatarGroup } from 'uiToolkitV1/AvatarGroup';
import { Button } from 'uiToolkitV1/Button';
import { Divider } from 'uiToolkitV1/Divider';
import InputLabel from 'uiToolkitV1/InputLabel';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { SearchEmployees } from './SearchEmployees';
import TooltipLabelList from './TooltipLabelList';
import { useLocalState } from './useLocalState';

export const OWNER_TYPES = {
  OWNER: 'OWNER',
  COLLABORATOR: 'COLLABORATOR',
};

const ControlComponent = forwardRef((props, ref) => {
  const { isOpen, onToggle, stakeHolders, isReadOnly, showName, isOwner, showSelectEmptyMessage, showIcon } = props;
  const noStakeholders = stakeHolders?.length === 0;

  return (
    <GenericInputControl
      ref={ref}
      onClick={isReadOnly ? null : onToggle}
      w="100%"
      $isActive={isOpen}
      $isReadOnly={isReadOnly}
    >
      {showIcon && (
        <Tooltip label={isOwner ? 'owners' : 'collaborators'} shouldWrapChildren>
          {isOwner ? (
            <PeerSettingsV2 boxSize="24px" fill="transparent" color="text.g2" mr="8px" />
          ) : (
            <Collaborators boxSize="24px" fill="transparent" color="text.g2" mr="8px" />
          )}{' '}
        </Tooltip>
      )}
      <Tooltip label={noStakeholders ? '' : <TooltipLabelList labelList={stakeHolders} />}>
        <Box>
          {noStakeholders ? (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {showSelectEmptyMessage ? (
                <Flex alignItems="center">
                  <Collaborators boxSize="18px" fill="transparent" color="text.g2" mr="8px" />
                  <TextBodyMedium color="text.g3">Select {isOwner ? 'owners' : 'collaborators'}</TextBodyMedium>
                </Flex>
              ) : (
                <TextBodyMedium>None</TextBodyMedium>
              )}
            </>
          ) : (
            <AvatarGroup>
              {stakeHolders.map(({ id, displayName, profilePhoto }) => (
                <Flex key={id} alignItems="center">
                  <Avatar w="22px" key={id} name={displayName} src={profilePhoto} mr="4px !important" />
                  {showName && (
                    <TextBodyMedium color="text.g1" mr="8px !important">
                      {displayName}
                    </TextBodyMedium>
                  )}
                </Flex>
              ))}
            </AvatarGroup>
          )}
        </Box>
      </Tooltip>
    </GenericInputControl>
  );
});

const Footer = ({ onApply, onCancel, isApplyDisabled }) => (
  <Flex justifyContent="space-between" alignItems="center" padding="12px 16px" background="#F5F7F9">
    <Button variant="tertiary" onClick={onCancel}>
      Cancel
    </Button>
    <Button disabled={isApplyDisabled} onClick={onApply}>
      Apply
    </Button>
  </Flex>
);

export const OKROwnerAndCollaboratorComponent = ({
  isOwner,
  ownersAndCollaborators = [],
  onChange = () => {},
  isReadOnly,
  showName,
  showIcon = false,
  showSelectEmptyMessage,
}) => {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const { moduleName } = useOrganisationSettings();

  const [searchText, setSearchText] = useState('');
  const [popupWidth, setPopupWidth] = useState(0);
  const [popupOffset, setOffset] = useState(0);
  const [popupControlComponentElement, setPopupControlComponentElement] = useState(null);
  const handleInputChange = useCallback((e) => setSearchText(e.target.value), []);
  const handleClearInput = useCallback(() => {
    setSearchText('');
  }, []);

  const searchRef = useRef();

  const [owners, collaborators] = useMemo(() => {
    const owners = ownersAndCollaborators
      .filter(({ ownerType }) => ownerType === OWNER_TYPES.OWNER)
      .map((emp) => ({ ...emp, isSelected: true }));
    const collaborators = ownersAndCollaborators
      .filter(({ ownerType }) => ownerType === OWNER_TYPES.COLLABORATOR)
      .map((emp) => ({ ...emp, isSelected: true }));

    return [owners, collaborators];
  }, [ownersAndCollaborators]);

  const stakeHolders = isOwner ? owners : collaborators;

  const [localStakeholders, setLocalStakeholders] = useLocalState(isOwner ? owners : collaborators);

  useEffect(() => {
    if (!isOpen) {
      const oldOwnersIds = owners.map(({ id }) => Number(id));
      const oldCollaboratorIds = collaborators.map(({ id }) => Number(id));

      if (isOwner) {
        const ownerIds = localStakeholders.map(({ id }) => Number(id));

        /* following condition checks if no change is made and returns early */

        if (oldOwnersIds.length === ownerIds.length && difference(oldOwnersIds, ownerIds).length === 0) return;

        const filteredCollaborators = collaborators.filter(({ id }) => !ownerIds.includes(Number(id)));

        const mappedOwners = localStakeholders.map(({ id, displayName, profilePhoto, email }) => ({
          id: Number(id),
          displayName,
          profilePhoto,
          ownerType: OWNER_TYPES.OWNER,
          email,
        }));

        const updateOwnerAndCollaborators = [...mappedOwners, ...filteredCollaborators];

        onChange(updateOwnerAndCollaborators);
      } else {
        const collaboratorIds = localStakeholders.map(({ id }) => Number(id));

        /* following condition checks if no change is made and returns early */
        if (
          oldCollaboratorIds.length === collaboratorIds.length &&
          difference(oldCollaboratorIds, collaboratorIds).length === 0
        )
          return;

        const filterOwners = owners.filter(({ id }) => !collaboratorIds.includes(Number(id)));

        const mappedCollaborators = localStakeholders.map(({ id, displayName, profilePhoto, email }) => ({
          id: Number(id),
          displayName,
          profilePhoto,
          ownerType: OWNER_TYPES.COLLABORATOR,
          email,
        }));

        onChange([...filterOwners, ...mappedCollaborators]);
      }
    }
  }, [isOpen, owners, collaborators, localStakeholders, isOwner]);

  const noStakeholders = stakeHolders?.length === 0;

  const onCancel = () => {
    handleClearInput();
    setLocalStakeholders(stakeHolders);
    onToggle();
  };

  const onApply = () => {
    handleClearInput();
    onClose();
  };

  const isDisabled = useMemo(() => {
    const originalStakeHolderIds = stakeHolders.map((data) => data.id);
    const localStakeHolderIds = localStakeholders.map((data) => data.id);

    return (
      originalStakeHolderIds.length === localStakeHolderIds.length &&
      originalStakeHolderIds.every((stakeHolder, i) => stakeHolder === localStakeHolderIds[i])
    );
  }, [localStakeholders, stakeHolders]);

  const onDelete = (e, employee) => {
    const isEmployeeAlreadyPresent = localStakeholders.findIndex(({ id }) => id === employee.id) > -1;

    if (isEmployeeAlreadyPresent && localStakeholders.length === 1 && isOwner) return;

    if (isEmployeeAlreadyPresent) setLocalStakeholders(localStakeholders.filter(({ id }) => id !== employee.id));
    // else setLocalStakeholders((prev) => [{ ...employee, isSelected: true }, ...prev]);
  };

  const controlComponentRef = useCallback((node) => {
    if (node !== null) {
      setPopupControlComponentElement(node);
      if (isMobileView()) {
        setPopupWidth(window.innerWidth);
        setOffset(node.getBoundingClientRect().top - document.body.scrollHeight); // Aligning bottom of popup to the bottom of viewport on mobile screens
      }
    }
  }, []);

  const handleResize = useCallback(() => {
    if (isMobileView() && popupControlComponentElement) {
      setPopupWidth(window.innerWidth);
      setOffset(popupControlComponentElement.getBoundingClientRect().top - document.body.scrollHeight); // Aligning bottom of popup to the bottom of viewport on mobile screens
    }
  }, [popupControlComponentElement]);

  const handleResizeDebounced = useMemo(() => debounce(handleResize, 250), [handleResize]);

  useEffect(() => {
    window.addEventListener('resize', handleResizeDebounced);

    return () => {
      window.removeEventListener('resize', handleResizeDebounced);
    };
  }, [handleResizeDebounced]);

  const mobileViewProps = isMobileView() ? { width: popupWidth, minW: popupWidth } : {};

  return (
    <Box className="owners-collabs">
      <Popover
        placement={isMobileView() ? 'top' : 'bottom-start'}
        flip
        returnFocusOnClose={false}
        isOpen={isOpen}
        initialFocusRef={isMobileView() ? null : searchRef}
        onClose={onCancel}
        boundary="scrollParent"
        preventOverflow
        offset={[0, popupOffset]}
      >
        <PopoverTrigger>
          <ControlComponent
            stakeHolders={localStakeholders}
            onToggle={onToggle}
            isOpen={isOpen}
            isReadOnly={isReadOnly}
            ref={controlComponentRef}
            showName={showName}
            showIcon={showIcon}
            isOwner={isOwner}
            showSelectEmptyMessage={showSelectEmptyMessage}
          />
        </PopoverTrigger>
        <PopoverContent minW="420px" boxShadow="0px 2px 8px 0px rgba(0, 0, 0, 0.08) !important" {...mobileViewProps}>
          <Box padding={2}>
            <SearchBar
              value={searchText}
              placeholder={`Search people to add as ${isOwner ? 'owners' : 'collaborators'} to the ${modifyString(
                moduleName,
                StringModifier.SENTENCE_CASE
              )}`}
              onChange={handleInputChange}
              searchRef={searchRef}
              onClearInput={handleClearInput}
            />
            <Box py={3} pl={1}>
              <Flex alignItems="center" columnGap={2}>
                <TextBodyMedium color="#666666" bold>
                  {`Selected (${localStakeholders?.length}) : `}
                </TextBodyMedium>
                <AvatarGroup size={24}>
                  {localStakeholders.map((emp) => {
                    const { id, displayName, profilePhoto } = emp;
                    const isAtleastOneEmployeePresent =
                      localStakeholders.findIndex(({ id }) => id === emp.id) > -1 && localStakeholders?.length === 1;

                    const isOwnerAndAtleastOneEmployeePresent = isOwner && isAtleastOneEmployeePresent;

                    return (
                      <Avatar isClearable key={id} name={displayName} src={profilePhoto}>
                        <Tooltip
                          label={isOwnerAndAtleastOneEmployeePresent ? 'At least one owner should be selected' : ''}
                        >
                          <AvatarBadge
                            style={{ top: '-10px', bottom: 'unset' }}
                            boxSize="0.9rem"
                            cursor={
                              isOwnerAndAtleastOneEmployeePresent ? 'not-allowed !important' : 'pointer !important'
                            }
                            onClick={(e) => {
                              onDelete(e, emp);
                            }}
                            bg="white"
                          >
                            <Cross boxSize="12px" color="#666666" />
                          </AvatarBadge>
                        </Tooltip>
                      </Avatar>
                    );
                  })}
                </AvatarGroup>
              </Flex>
            </Box>
            <Divider />
          </Box>
          <PopoverBody px={2} overflowY="auto" height={isMobileView() ? 'calc(100vh - 180px)' : '150px'}>
            <SearchEmployees
              isOwner={isOwner}
              employees={localStakeholders}
              originalOwners={owners}
              setEmployees={setLocalStakeholders}
              searchText={searchText}
              atLeastOneEmployeeToBeSelected={isOwner}
            />
          </PopoverBody>
          <Divider />
          <Footer isApplyDisabled={isDisabled} onApply={onApply} onCancel={onCancel} />
        </PopoverContent>
      </Popover>
    </Box>
  );
};

const OKROwnerAndCollaborator = ({
  labelText = 'Owner',
  isOwner = false,
  ownersAndCollaborators = [],
  onChange = () => {},
  isReadOnly,
}) => (
  <InputLabel labelText={labelText}>
    <OKROwnerAndCollaboratorComponent
      isOwner={isOwner}
      ownersAndCollaborators={ownersAndCollaborators}
      onChange={onChange}
      isReadOnly={isReadOnly}
    />
  </InputLabel>
);

export default OKROwnerAndCollaborator;
